import React, { useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import {
  MCSR_COLUMN_CAPTIONS,
  MEASURE_KEYS,
  McsrReportPayload,
  McsrReportRow,
  fmtMcsr,
  fmtMcsrPercent,
} from '../../types/mcsr';

/**
 * Faithful on-screen reproduction of the MCSR report:
 *   header block → Section A summary grid → Section B Financial Analysis
 *   (band headers with the deliberate Planned/Actual order swap, red title,
 *   yellow highlight cells) → Section C detail grid with collapsible cost
 *   groups and pinned totals. Numbers render #,##0.000, negatives keep the
 *   minus sign, right-aligned.
 */

interface Props {
  payload: McsrReportPayload;
  onDrillGroup?: (costGroup: string) => void;
  onDrillCostType?: (row: McsrReportRow) => void;
}

const numSx = { textAlign: 'right', whiteSpace: 'nowrap', fontSize: 12, py: 0.4, px: 0.75 } as const;
const negSx = { ...numSx, color: '#b00020' } as const;

function MeasureCells({ row, bold }: { row: McsrReportRow; bold?: boolean }) {
  return (
    <>
      {MEASURE_KEYS.map((k) => {
        const v = row[k];
        return (
          <TableCell key={k} sx={{ ...(v < 0 ? negSx : numSx), fontWeight: bold ? 700 : 400 }}>
            {fmtMcsr(v)}
          </TableCell>
        );
      })}
    </>
  );
}

function HeaderCells({ leading }: { leading: string[] }) {
  return (
    <TableRow>
      {leading.map((c) => (
        <TableCell key={c || 'blank'} sx={{ fontWeight: 700, fontSize: 11, minWidth: 110, verticalAlign: 'bottom' }}>
          {c}
        </TableCell>
      ))}
      {MCSR_COLUMN_CAPTIONS.map((c) => (
        <TableCell key={c} sx={{ fontWeight: 700, fontSize: 10.5, textAlign: 'center', minWidth: 108, verticalAlign: 'bottom' }}>
          {c}
        </TableCell>
      ))}
    </TableRow>
  );
}

export function McsrSummaryGrid({ payload, onDrillGroup }: Props) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}>
      <Table size="small" stickyHeader aria-label="MCSR summary grid">
        <TableHead>
          <HeaderCells leading={['Classification', 'Cost Group', 'Project Cost Type']} />
        </TableHead>
        <TableBody>
          {payload.summary.map((r, i) => {
            if (r.kind === 'detail') {
              return (
                <TableRow
                  key={i}
                  hover
                  onClick={onDrillGroup && r.costGroup ? () => onDrillGroup(r.costGroup!) : undefined}
                  sx={{ cursor: onDrillGroup ? 'pointer' : undefined }}
                >
                  <TableCell sx={{ fontSize: 12 }}>{r.classificationName}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.costGroup}</TableCell>
                  <TableCell />
                  <MeasureCells row={r} />
                </TableRow>
              );
            }
            const isGrand = r.kind === 'grand-total';
            return (
              <TableRow key={i} sx={{ bgcolor: isGrand ? 'grey.200' : 'grey.50' }}>
                <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: isGrand ? 13 : 12 }}>
                  {isGrand ? 'GRAND TOTAL:' : 'SUB TOTAL:'}
                </TableCell>
                <MeasureCells row={r} bold />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function McsrFinancialAnalysis({ payload }: { payload: McsrReportPayload }) {
  const fa = payload.financialAnalysis;
  const hl = { bgcolor: '#FFFF00' } as const;
  const cell = (v: number | null, opts: { pct?: boolean; highlight?: boolean } = {}) => (
    <TableCell sx={{ ...numSx, ...(opts.highlight ? hl : {}), fontWeight: opts.pct ? 700 : 400 }}>
      {v === null ? '' : opts.pct ? fmtMcsrPercent(v) : fmtMcsr(v)}
    </TableCell>
  );
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: 760 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#FF0000', mb: 1 }}>
        Financial Analysis Report
      </Typography>
      <Table size="small" aria-label="Financial analysis">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell colSpan={2} sx={{ textAlign: 'center', fontWeight: 700 }}>This Period</TableCell>
            <TableCell colSpan={2} sx={{ textAlign: 'center', fontWeight: 700 }}>To Date</TableCell>
          </TableRow>
          <TableRow>
            <TableCell />
            {/* Deliberate order swap: This Period → Planned, Actual; To Date → Actual, Planned */}
            <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>Planned</TableCell>
            <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>Actual</TableCell>
            <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>Actual</TableCell>
            <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>Planned</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>1 - Work Done</TableCell>
            {cell(fa.workDone.periodPlanned)}
            {cell(fa.workDone.periodActual)}
            {cell(fa.workDone.toDateActual, { highlight: true })}
            {cell(fa.workDone.toDatePlanned)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>2 - Provisional Work Done</TableCell>
            {cell(null)}
            {cell(fa.provisionalWorkDone.periodActual)}
            {cell(null)}
            {cell(null)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>3 - Sundry Income</TableCell>
            {cell(null)}
            {cell(fa.sundryIncome.periodActual)}
            {cell(fa.sundryIncome.toDateActual)}
            {cell(null)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>4 - Total Income</TableCell>
            {cell(null)}
            {cell(fa.totalIncome.periodActual)}
            {cell(fa.totalIncome.toDateActual)}
            {cell(null)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>5 - Total cost (TD-Store value in MCSR)</TableCell>
            {cell(null)}
            {cell(fa.totalCost.periodActual)}
            {cell(fa.totalCost.toDateActual)}
            {cell(null)}
          </TableRow>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>6 - Overall Profit</TableCell>
            {cell(null)}
            {cell(fa.overallProfit.periodActual, { pct: true, highlight: true })}
            {cell(fa.overallProfit.toDateActual, { pct: true, highlight: true })}
            {cell(null)}
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  );
}

interface DetailGroup {
  costGroup: string;
  classificationName: string | null;
  rows: McsrReportRow[];
  total: McsrReportRow | null;
}

export function McsrDetailGrid({
  payload,
  focusGroup,
  onDrillCostType,
}: Props & { focusGroup?: string | null }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Single pass over the ordered detail rows → interleaved blocks:
  // cost-group blocks (leaf rows + their TOTAL:<group> row) and standalone
  // total rows (TOTAL:<classification>, GRAND TOTAL:) in query order.
  const orderedBlocks = useMemo(() => {
    const blocks: Array<{ type: 'group'; group: DetailGroup } | { type: 'total'; row: McsrReportRow }> = [];
    let current: DetailGroup | null = null;
    for (const r of payload.detail) {
      if (r.kind === 'detail') {
        if (!current || current.costGroup !== (r.costGroup || '')) {
          current = { costGroup: r.costGroup || '', classificationName: r.classificationName, rows: [], total: null };
          blocks.push({ type: 'group', group: current });
        }
        current.rows.push(r);
      } else if (r.kind === 'group-total') {
        if (current && (r.costGroup || '') === current.costGroup) {
          current.total = r;
        } else {
          blocks.push({ type: 'group', group: { costGroup: r.costGroup || '', classificationName: r.classificationName, rows: [], total: r } });
        }
        current = null;
      } else {
        blocks.push({ type: 'total', row: r });
        current = null;
      }
    }
    if (focusGroup) {
      return blocks.filter((b) => b.type === 'group' && b.group.costGroup === focusGroup);
    }
    return blocks;
  }, [payload.detail, focusGroup]);

  const toggle = (key: string) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="MCSR detail grid">
        <TableHead>
          <HeaderCells leading={['', 'Cost Group', 'Project Cost Type']} />
        </TableHead>
        <TableBody>
          {orderedBlocks.map((block, bi) => {
            if (block.type === 'total') {
              const r = block.row;
              const isGrand = r.kind === 'grand-total';
              return (
                <TableRow key={`t${bi}`} sx={{ bgcolor: isGrand ? 'grey.300' : 'grey.100' }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: isGrand ? 13 : 12 }}>
                    {isGrand ? 'GRAND TOTAL:' : r.label}
                  </TableCell>
                  <MeasureCells row={r} bold />
                </TableRow>
              );
            }
            const g = block.group;
            const isCollapsed = !!collapsed[g.costGroup];
            return (
              <React.Fragment key={`g${g.costGroup}`}>
                {g.rows.map((r, ri) => (
                  <TableRow
                    key={ri}
                    hover
                    onClick={onDrillCostType ? () => onDrillCostType(r) : undefined}
                    sx={{
                      cursor: onDrillCostType ? 'pointer' : undefined,
                      display: isCollapsed ? 'none' : undefined,
                    }}
                  >
                    {ri === 0 ? (
                      <TableCell sx={{ width: 34, p: 0 }}>
                        <IconButton
                          size="small"
                          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${g.costGroup}`}
                          onClick={(e) => { e.stopPropagation(); toggle(g.costGroup); }}
                        >
                          {isCollapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    ) : (
                      <TableCell sx={{ p: 0 }} />
                    )}
                    <TableCell sx={{ fontSize: 12 }}>{r.costGroup}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.projectCostType}</TableCell>
                    <MeasureCells row={r} />
                  </TableRow>
                ))}
                {isCollapsed && (
                  <TableRow>
                    <TableCell sx={{ width: 34, p: 0 }}>
                      <IconButton
                        size="small"
                        aria-label={`Expand ${g.costGroup}`}
                        onClick={() => toggle(g.costGroup)}
                      >
                        <KeyboardArrowRight fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell colSpan={2} sx={{ fontStyle: 'italic', fontSize: 12 }}>
                      {g.costGroup} ({g.rows.length} cost types hidden)
                    </TableCell>
                    <TableCell colSpan={15} />
                  </TableRow>
                )}
                {g.total && (
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: 12 }}>{g.total.label}</TableCell>
                    <MeasureCells row={g.total} bold />
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function McsrReportView(props: Props & { focusGroup?: string | null }) {
  const { payload } = props;
  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 1.5 }}>
        <Typography sx={{ fontFamily: 'Tahoma, sans-serif', fontWeight: 700, fontSize: 17 }}>
          {payload.header.companyName}
        </Typography>
        <Typography sx={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, fontSize: 15 }}>
          {payload.header.title}
        </Typography>
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          Customer:&nbsp;&nbsp;{payload.header.customerCode}&nbsp;&nbsp;{payload.header.customerName}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
          Project: {payload.header.project} / {payload.header.projectName}
        </Typography>
      </Box>
      <McsrSummaryGrid {...props} />
      <McsrFinancialAnalysis payload={payload} />
      <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 2, mb: 0.5 }}>Detail</Typography>
      <McsrDetailGrid {...props} />
    </Box>
  );
}
